import axios from 'axios'
import {formatApiError} from '../../../shared/utils/apiErrorMessage'
import {extractRows} from '../../../shared/access/tenantScopeMatch'
import {
  Company,
  CompanyAdmin,
  Vessel,
  VesselParticulars,
  VesselGeneralInfo,
  VesselGeneralInfoRequest,
  ForUSACalls,
  ForUSACallsRequest,
  SafetyHelicopterRequest,
  SafetyHelicopter,
  TankCoating,
  TankCoatingRequest,
  Ballast,
  BallastRequest,
  CargoSystem,
  CargoSystemRequest,
  VacuumSystem,
  VacuumSystemRequest,
  PropulsionSystem,
  PropulsionSystemRequest,
  VesselTanksOption,
  VesselTanksOptionRequest,
  VesselLandingResponse ,
  Operator, CreateOperatorRequest, UpdateOperatorRequest, OperatorSubRole,
  CreateCompanyOwnerRequest,
  AssignableRoleForUserCreate,
  Vendor, VendorCategory, CreateVendorRequest, UpdateVendorRequest, VendorImportResult, VendorImportPreview, RegionOption,
  RegionTreeNode,
  RegionMutationRequest,
  ManagementUser,
  ScopedUser
} from './_models'
import type {VendorDeliveryPage} from './_models'
import type { MenuTreeNode } from '../../../shared/types/menu-tree'
export type { MenuTreeNode } from '../../../shared/types/menu-tree'

const API_URL = process.env.REACT_APP_API_URL
const mgmtErr = (error: unknown, fallback: string) => new Error(formatApiError(error, fallback))

const normalizeMirrorPassword = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('$2a$') || trimmed.startsWith('$2b$') || trimmed.startsWith('$2y$')) {
    return null
  }
  return trimmed
}

/** Company group admins (Companies menu entity: CompanyGroupAdmin). */
const COMPANY_GROUP_ADMINS_API_URL = `${API_URL}/company-group-admins`
/** Sub-companies (Sub-Companies menu entity: CompanyAdmin). */
const COMPANY_ADMINS_API_URL = `${API_URL}/company-admins`
const USERS_API_URL = `${API_URL}/users`
const VESSEL_API_URL = `${API_URL}/vessels`
const ADDITIONAL_VESSEL_API_URL = `${API_URL}/v1/vessels/general-information`
const VESSEL_PARTICULARS_API_URL = `${API_URL}/v1/vessels/particulars`
const FOR_USA_CALLS_API_URL = `${API_URL}/v1/vessels/for-usa-calls`
const SAFETY_HELI_API_URL = `${API_URL}/v1/vessels/safety-helicopter`
const TANK_COATING_API_URL = `${API_URL}/v1/vessels/tank-coating`
const BALLAST_API_BASE = `${API_URL}/v1/vessels`
const CARGO_API_BASE = `${API_URL}/v1/vessels`
const VACUUM_API_BASE = `${API_URL}/v1/vessels`
const PROPULSION_API_BASE = `${API_URL}/v1/vessels`
const TANKS_OPTION_API_BASE = `${API_URL}/v1/vessels`
const VESSEL_LANDING_API_URL = `${API_URL}/vessels/landing`
const OPERATORS_API_URL = `${API_URL}/operators`
const OPERATOR_SUB_ROLES_API_URL = `${API_URL}/operator-sub-roles`
const RANKS_API_URL = `${API_URL}/ranks`
const ROLES_API_URL = `${API_URL}/roles`
const SHORES_API_URL = `${API_URL}/shores`
const MANAGEMENT_USERS_API_URL = `${API_URL}/users/management`
const CURRENCIES_API_URL = `${API_URL}/currencies`

const normalizeAssignableRoleListResponse = (raw: any): AssignableRoleForUserCreate[] => {
  const items = raw?.content ?? raw?.items ?? raw?.data ?? raw
  return Array.isArray(items) ? items : []
}

export const getAssignableRolesForUserCreate = async (
  activeOnly = false,
  companyGroupAdminId?: number | null
): Promise<AssignableRoleForUserCreate[]> => {
  try {
    const res = await axios.get<AssignableRoleForUserCreate[]>(
      `${ROLES_API_URL}/options/assignable-for-user-create`,
      {
        params: {
          activeOnly,
          ...(companyGroupAdminId != null && Number.isFinite(Number(companyGroupAdminId))
            ? { companyGroupAdminId: Number(companyGroupAdminId) }
            : {}),
        },
      }
    )
    return normalizeAssignableRoleListResponse(res.data)
  } catch (error: any) {
    console.error('Error loading assignable roles:', error)
    throw new Error(
      error.response?.data?.message || error.response?.data?.error || 'Failed to load assignable roles'
    )
  }
}

export const getOperatorSubRoles = async (): Promise<OperatorSubRole[]> => {
  try {
    const res = await axios.get<OperatorSubRole[]>(OPERATOR_SUB_ROLES_API_URL)
    return res.data
  } catch (error: any) {
    console.error('Error fetching operator sub-roles:', error)
    throw new Error(error.response?.data?.message || 'Error fetching operator sub-roles')
  }
}

export type CrewRankOption = {
  id: number
  rank: string
}

export type OperatorSubRoleUpsertRequest = {
  code: string
  name: string
  description?: string | null
  sortOrder?: number | null
  active?: boolean
}

export const createOperatorSubRole = async (
  payload: OperatorSubRoleUpsertRequest
): Promise<OperatorSubRole> => {
  try {
    const res = await axios.post<OperatorSubRole>(OPERATOR_SUB_ROLES_API_URL, payload)
    return res.data
  } catch (error: any) {
    console.error('Error creating operator sub-role:', error)
    throw new Error(error.response?.data?.message || 'Error creating operator sub-role')
  }
}

export const updateOperatorSubRole = async (
  id: number,
  payload: OperatorSubRoleUpsertRequest
): Promise<OperatorSubRole> => {
  try {
    const res = await axios.put<OperatorSubRole>(`${OPERATOR_SUB_ROLES_API_URL}/${id}`, payload)
    return res.data
  } catch (error: any) {
    console.error('Error updating operator sub-role:', error)
    throw new Error(error.response?.data?.message || 'Error updating operator sub-role')
  }
}

export const getOperatorSubRoleDefaultPackageIds = async (id: number): Promise<number[]> => {
  try {
    const res = await axios.get<number[]>(`${OPERATOR_SUB_ROLES_API_URL}/${id}/default-package`)
    return Array.isArray(res.data) ? res.data : []
  } catch (error: any) {
    console.error('Error loading operator sub-role default packages:', error)
    throw new Error(error.response?.data?.message || 'Error loading operator sub-role default packages')
  }
}

export const saveOperatorSubRoleDefaultPackageIds = async (
  id: number,
  bundleIds: number[]
): Promise<number[]> => {
  try {
    const res = await axios.post<number[]>(
      `${OPERATOR_SUB_ROLES_API_URL}/${id}/default-package`,
      { bundleIds }
    )
    return Array.isArray(res.data) ? res.data : []
  } catch (error: any) {
    console.error('Error saving operator sub-role default packages:', error)
    throw new Error(error.response?.data?.message || 'Error saving operator sub-role default packages')
  }
}

export const getCrewRanksForDropdown = async (): Promise<CrewRankOption[]> => {
  try {
    const res = await axios.get<CrewRankOption[]>(`${RANKS_API_URL}/drop-down`)
    return Array.isArray(res.data) ? res.data : []
  } catch (error: any) {
    console.error('Error loading crew ranks:', error)
    throw new Error(error.response?.data?.message || 'Error loading crew ranks')
  }
}

export const getCrewBaseRanksForPermissionDropdown = async (): Promise<CrewRankOption[]> => {
  try {
    const res = await axios.get<CrewRankOption[]>(`${RANKS_API_URL}/crew-base-permission-options`)
    return Array.isArray(res.data) ? res.data : []
  } catch (error: any) {
    console.error('Error loading crew base ranks:', error)
    throw new Error(error.response?.data?.message || 'Error loading crew base ranks')
  }
}


// ——— VESSELS ——————————————————————————————————————————————————————————————————

export const getVesselList = async (): Promise<Vessel[]> => {
  try {
    const response = await axios.get<Vessel[]>(VESSEL_API_URL)
    return response.data
  } catch (error: any) {
    console.error('Error fetching vessel list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel list')
  }
}

export const createVessel = async (
  vesselName: string,
  vesselType: string,
  imoNumber: string,
  officialNumber: string,
  mmsi: string,
  call_sign: string,
  flag: string,
  portOfRegistry: string,
  classes: string,
  classNotation: string,
  piClub: string,
  area: string,
  dwt: string,
  vesselPhoneNumber: string,
  vesselEmailAddress: string,
  vesselPhoneNumbers: string[],
  vesselEmails: string[],
  companyGroupAdminId: number,
  companyAdminId?: number,
  naFields?: Record<string, boolean>,
  vesselInitials?: string
): Promise<Vessel> => {
  try {
    const requestBody: any = {
      fleet_name: vesselName,
      vesselInitials: vesselInitials?.trim().toUpperCase() || null,
      vesselType,
      imoNumber: imoNumber?.trim() || null,
      officialNumber: officialNumber?.trim() || null,
      mmsi,
      call_sign,
      flag,
      portOfRegistry,
      classes,
      classNotation,
      piClub,
      area,
      dwt,
      vesselPhoneNumber,
      vesselEmailAddress,
      vesselPhoneNumbers,
      vesselEmails,
      companyGroupAdminId,
      active: true,
    }

    if (companyAdminId) {
      requestBody.companyAdminId = companyAdminId
    }

    const response = await axios.post<Vessel>(`${VESSEL_API_URL}/create`, requestBody)
    return response.data
  } catch (error: any) {
    console.error('Error creating vessel:', error)
    throw new Error(error.response?.data?.message || 'Error creating vessel')
  }
}

export const updateVessel = async (
  vesselId: number,
  vesselName: string,
  vesselType: string,
  imoNumber: string,
  officialNumber: string,
  mmsi: string,
  call_sign: string,
  flag: string,
  portOfRegistry: string,
  classes: string,
  classNotation: string,
  piClub: string,
  area: string,
  dwt: string,
  vesselPhoneNumber: string,
  vesselEmailAddress: string,
  vesselPhoneNumbers: string[],
  vesselEmails: string[],
  companyAdminId: number,
  companyGroupAdminId: number,
  isActive: boolean,
  naFields?: Record<string, boolean>,
  vesselInitials?: string
): Promise<Vessel> => {
  try {
    const payload: any = {
      fleet_name: vesselName,
      vesselInitials: vesselInitials?.trim().toUpperCase() || null,
      vesselType,
      imoNumber: imoNumber?.trim() || null,
      officialNumber: officialNumber?.trim() || null,
      mmsi,
      call_sign,
      flag,
      portOfRegistry,
      classes,
      classNotation,
      piClub,
      area,
      dwt,
      vesselPhoneNumber,
      vesselEmailAddress,
      vesselPhoneNumbers,
      vesselEmails,
      companyGroupAdminId,
      active: isActive,
    }
    // ✅ Add companyAdminId only if it's not 0
    if (companyAdminId !== 0) {
      payload.companyAdminId = companyAdminId
    }
    const response = await axios.put<Vessel>(`${VESSEL_API_URL}/update/${vesselId}`, payload)

    return response.data
  } catch (error: any) {
    console.error('Error updating vessel:', error)
    throw new Error(error.response?.data?.message || 'Error updating vessel')
  }
}

export const deleteVessel = async (vesselId: number): Promise<Vessel[]> => {
  try {
    const response = await axios.delete<Vessel[]>(`${VESSEL_API_URL}/delete/${vesselId}`)
    return response.data
  } catch (error: any) {
    console.error('Error deleting vessel item:', error)
    throw new Error(error.response?.data?.message || 'Error deleting vessel item')
  }
}

// ——— CompanyGroupAdmin (Companies menu — route /manage/companies) ———
// Entity: CompanyGroupAdmin | Permissions: *_COMPANY_GROUP_ADMIN
// API family: /api/company-group-admins

/** Row from GET /api/company-group-admins. Use id as roles.companyGroupAdminId. */
export type CompanyGroupAdminListItem = {
  id: number
  name?: string | null
  companyGroupAdminDetails?: string | null
  isActive?: boolean
  active?: boolean
}

export const getCompanyGroupAdmins = async (): Promise<CompanyAdmin[]> => {
  try {
    const response = await axios.get(COMPANY_GROUP_ADMINS_API_URL)
    const rows = extractRows<CompanyAdmin>(response.data)
    return rows.map((row) => ({
      ...row,
      active: row.active ?? (row as { isActive?: boolean }).isActive ?? true,
      companyDetails:
        row.companyDetails ??
        row.companyGroupAdminDetails ??
        '',
    }))
  } catch (error: any) {
    console.error('Error fetching company group admins:', error)
    throw new Error(
      error.response?.data?.message || error.response?.data?.error || 'Error fetching companies'
    )
  }
}

/** @deprecated Legacy misname — loads CompanyGroupAdmin rows for the Companies menu. Prefer getCompanyGroupAdmins. */
export const getCompanyAdminList = getCompanyGroupAdmins

/** @deprecated Prefer getCompanyGroupAdmins. Minimal row shape for role/tenant pickers. */
export const getCompanyGroupAdminList = async (): Promise<CompanyGroupAdminListItem[]> => {
  try {
    const response = await axios.get<CompanyGroupAdminListItem[]>(COMPANY_GROUP_ADMINS_API_URL)
    return Array.isArray(response.data) ? response.data : extractRows<CompanyGroupAdminListItem>(response.data)
  } catch (error: any) {
    console.error('Error fetching company group admin list:', error)
    throw new Error(
      error.response?.data?.message || error.response?.data?.error || 'Error fetching company groups'
    )
  }
}

// ——— CompanyAdmin (Sub-Companies menu — route /manage/subcompanies) ———
// Entity: CompanyAdmin | Permissions: *_COMPANY_ADMIN
// API family: /api/company-admins

export const getCompanyAdmins = async (): Promise<Company[]> => {
  try {
    const response = await axios.get<Company[]>(COMPANY_ADMINS_API_URL)
    return extractRows<Company>(response.data)
  } catch (error: any) {
    console.error('Error fetching sub-companies:', error)
    throw new Error(error.response?.data?.message || 'Error fetching sub-companies')
  }
}

/** @deprecated Legacy name — loads CompanyAdmin rows for Sub-Companies. Prefer getCompanyAdmins. */
export const getCompanyList = getCompanyAdmins

export const getManagementUsers = async (): Promise<ManagementUser[]> => {
  try {
    const response = await axios.get<ManagementUser[]>(MANAGEMENT_USERS_API_URL)
    return Array.isArray(response.data)
      ? response.data.map((row) => {
          const displayPassword = normalizeMirrorPassword(
            row.displayPassword ?? row.password ?? (row as any)?.uid?.password
          )
          return {
            ...row,
            password: '',
            displayPassword,
            passwordSet: Boolean(displayPassword) || row.passwordSet === true,
          }
        })
      : []
  } catch (error: any) {
    console.error('Error fetching management users:', error)
    throw mgmtErr(error, 'Error fetching management users')
  }
}

export interface UpdateManagementUserPayload {
  fullName?: string
  email?: string
  mobile?: string
  enabled?: boolean
  operatorSubRoleId?: number | null
  dateOfBirth?: string
}

export const updateManagementUser = async (
  userId: number,
  payload: UpdateManagementUserPayload
): Promise<ManagementUser> => {
  const response = await axios.put<ManagementUser>(`${MANAGEMENT_USERS_API_URL}/${userId}`, payload)
  return response.data
}

export type VesselAccessMode = 'ALL' | 'ASSIGNED' | 'NONE'

export type AssignableVessel = {
  id: number
  name: string
  imoNumber?: string | null
  companyGroupAdminId?: number | null
  companyAdminId?: number | null
}

export type UserVesselAccessResponse = {
  userId: number
  accessMode: VesselAccessMode
  vessels: AssignableVessel[]
}

export type UpdateUserVesselAccessRequest = {
  accessMode: VesselAccessMode
  vesselIds: number[]
}

export const getUserVesselAccess = async (userId: number): Promise<UserVesselAccessResponse> => {
  try {
    const response = await axios.get<UserVesselAccessResponse>(`${USERS_API_URL}/${userId}/vessel-access`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching user vessel access:', error)
    throw mgmtErr(error, 'Failed to load vessel access')
  }
}

export const updateUserVesselAccess = async (
  userId: number,
  payload: UpdateUserVesselAccessRequest
): Promise<UserVesselAccessResponse> => {
  try {
    const response = await axios.put<UserVesselAccessResponse>(`${USERS_API_URL}/${userId}/vessel-access`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating user vessel access:', error)
    throw mgmtErr(error, 'Failed to update vessel access')
  }
}

export const getAssignableVesselsForUser = async (userId: number): Promise<AssignableVessel[]> => {
  try {
    const response = await axios.get<AssignableVessel[]>(`${USERS_API_URL}/${userId}/assignable-vessels`)
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching assignable vessels:', error)
    throw mgmtErr(error, 'Failed to load assignable vessels')
  }
}

export const getAccessibleVesselsForMe = async (): Promise<AssignableVessel[]> => {
  try {
    const response = await axios.get<AssignableVessel[]>(`${API_URL}/me/accessible-vessels`)
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching accessible vessels:', error)
    throw mgmtErr(error, 'Failed to load accessible vessels')
  }
}

export const resetUserPassword = async (
  userId: number,
  payload: { newPassword: string; confirmPassword: string }
): Promise<void> => {
  await axios.post(`${API_URL}/users/${userId}/reset-password`, payload)
}

export const sendUserPasswordResetLink = async (userId: number): Promise<void> => {
  await axios.post(`${API_URL}/users/${userId}/send-password-reset-link`)
}

export const getUsersForRole = async (): Promise<ScopedUser[]> => {
  try {
    const response = await axios.get<ScopedUser[]>(USERS_API_URL)
    return response.data
  } catch (error: any) {
    console.error('Error fetching scoped users:', error)
    throw new Error(error.response?.data?.message || 'Error fetching scoped users')
  }
}

export const createCompanyAdmin = async (
  companyName: string,
  username: string,
  rawPassword: string,
  contactNo: string,
  altContactNo: string,
  email: string,
  addressLine1: string,
  addressLine2: string,
  landmark: string,
  country: string,
  state: string,
  city: string,
  companyDetails: string,
  companyGroupAdminId: number,
  gstNumber?: string,
  panNumber?: string,
  vatNumber?: string
): Promise<Company> => {
  try {
    const response = await axios.post<Company>(`${COMPANY_ADMINS_API_URL}/create`, {
      uid: null,
      cgaid: companyGroupAdminId,
      name: companyName,
      contactNo,
      altContactNo,
      email,
      addressLine1,
      addressLine2,
      landmark,
      country,
      state,
      city,
      companyDetails,
      gstNumber,
      panNumber,
      vatNumber,
      username,
      rawPassword,
      active: true,
    })

    return response.data
  } catch (error: any) {
    console.error('Error creating sub-company:', error)
    throw new Error(error.response?.data?.message || 'Error creating sub-company')
  }
}

/** @deprecated Prefer createCompanyAdmin (Sub-Companies / CompanyAdmin entity). */
export const createCompany = createCompanyAdmin

export const updateCompanyAdmin = async (
  companyAdminId: number,
  companyGroupAdminId: number,
  companyName: string,
  contactNo: string,
  altContactNo: string,
  email: string,
  addressLine1: string,
  addressLine2: string,
  landmark: string,
  country: string,
  state: string,
  city: string,
  companyDetails: string,
  isActive?: boolean,
  gstNumber?: string,
  panNumber?: string,
  vatNumber?: string
): Promise<Company> => {
  try {
    const response = await axios.put<Company>(`${COMPANY_ADMINS_API_URL}/update/${companyAdminId}`, {
      id: companyAdminId,
      cgaid: companyGroupAdminId,
      name: companyName,
      contactNo,
      altContactNo,
      email,
      addressLine1,
      addressLine2,
      landmark,
      country,
      state,
      city,
      companyDetails,
      gstNumber,
      panNumber,
      vatNumber,
      active: isActive,
    })

    return response.data
  } catch (error: any) {
    console.error('Error updating sub-company:', error)
    throw new Error(error.response?.data?.message || 'Error updating sub-company')
  }
}

/** @deprecated Prefer updateCompanyAdmin (Sub-Companies / CompanyAdmin entity). */
export const updateCompany = updateCompanyAdmin

export const deleteCompanyAdmin = async (companyAdminId: number): Promise<Company[]> => {
  try {
    const response = await axios.delete<Company[]>(`${COMPANY_ADMINS_API_URL}/delete/${companyAdminId}`)
    return response.data
  } catch (error: any) {
    console.error('Error deleting sub-company:', error)
    throw new Error(error.response?.data?.message || 'Error deleting sub-company')
  }
}

/** @deprecated Prefer deleteCompanyAdmin (Sub-Companies / CompanyAdmin entity). */
export const deleteCompany = deleteCompanyAdmin

// ——— CompanyGroupAdmin mutations (Companies menu) ———

export const createCompanyGroupAdmin = async (
  companyName: string,
  username: string,
  password: string,
  contactNo: string,
  altContactNo: string,
  email: string,
  addressLine1: string,
  addressLine2: string,
  landmark: string,
  country: string,
  state: string,
  city: string,
  companyDetails: string,
  primaryCurrencyCode?: string,
  gstNumber?: string,
  panNumber?: string,
  vatNumber?: string
): Promise<CompanyAdmin> => {
  try {
    const response = await axios.post(COMPANY_GROUP_ADMINS_API_URL + '/create', {
      uid: null,
      name: companyName,
      contactNo,
      altContactNo,
      email,
      addressLine1,
      addressLine2,
      landmark,
      country,
      state,
      city,
      companyGroupAdminDetails: companyDetails,
      primaryCurrencyCode,
      gstNumber,
      panNumber,
      vatNumber,
      password,
      username,
      active: true,
    })

    return response.data
  } catch (error: any) {
    console.error('Error creating company group admin:', error)
    throw new Error(error.response?.data?.message || 'Error creating company')
  }
}

export const updateCompanyGroupAdmin = async (
  companyGroupAdminId: number,
  companyName: string,
  contactNo: string,
  altContactNo: string,
  email: string,
  addressLine1: string,
  addressLine2: string,
  landmark: string,
  country: string,
  state: string,
  city: string,
  companyDetails: string,
  primaryCurrencyCode?: string,
  isActive?: boolean,
  gstNumber?: string,
  panNumber?: string,
  vatNumber?: string
): Promise<CompanyAdmin> => {
  try {
    const response = await axios.put<CompanyAdmin>(
      `${COMPANY_GROUP_ADMINS_API_URL}/update/${companyGroupAdminId}`,
      {
        id: companyGroupAdminId,
        name: companyName,
        contactNo,
        altContactNo,
        email,
        addressLine1,
        addressLine2,
        landmark,
        country,
        state,
        city,
        companyGroupAdminDetails: companyDetails,
        primaryCurrencyCode,
        gstNumber,
        panNumber,
        vatNumber,
        active: isActive,
      }
    )

    return response.data
  } catch (error: any) {
    console.error('Error updating company group admin:', error)
    throw new Error(error.response?.data?.message || 'Error updating company')
  }
}

export const uploadCompanyGroupBrandingAsset = async (
  companyGroupAdminId: number,
  assetType: 'logo' | 'watermark',
  file: File
): Promise<CompanyAdmin> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    const response = await axios.put<CompanyAdmin>(
      `${COMPANY_GROUP_ADMINS_API_URL}/${companyGroupAdminId}/branding/${assetType}`,
      formData,
      {
        headers: {'Content-Type': 'multipart/form-data'},
      }
    )
    return response.data
  } catch (error: any) {
    console.error(`Error uploading company ${assetType}:`, error)
    throw new Error(error.response?.data?.message || `Error uploading company ${assetType}`)
  }
}

/** @deprecated Prefer uploadCompanyGroupBrandingAsset */
export const uploadCompanyBrandingAsset = uploadCompanyGroupBrandingAsset

export const deleteCompanyGroupBrandingAsset = async (
  companyGroupAdminId: number,
  assetType: 'logo' | 'watermark'
): Promise<CompanyAdmin> => {
  try {
    const response = await axios.delete<CompanyAdmin>(
      `${COMPANY_GROUP_ADMINS_API_URL}/${companyGroupAdminId}/branding/${assetType}`
    )
    return response.data
  } catch (error: any) {
    console.error(`Error deleting company ${assetType}:`, error)
    throw new Error(error.response?.data?.message || `Error deleting company ${assetType}`)
  }
}

/** @deprecated Prefer deleteCompanyGroupBrandingAsset */
export const deleteCompanyBrandingAsset = deleteCompanyGroupBrandingAsset

export const deleteCompanyGroupAdmin = async (companyGroupAdminId: number): Promise<CompanyAdmin[]> => {
  try {
    const response = await axios.delete<CompanyAdmin[]>(
      `${COMPANY_GROUP_ADMINS_API_URL}/delete/${companyGroupAdminId}`
    )

    return response.data
  } catch (error: any) {
    console.error('Error deleting company group admin:', error)
    throw new Error(error.response?.data?.message || 'Error deleting company')
  }
}

export const createGeneralInfoOfVessel = async (
  vesselId: number,
  payload: VesselGeneralInfoRequest
): Promise<VesselGeneralInfo> => {
  try {
    const response = await axios.post<VesselGeneralInfo>(
      `${ADDITIONAL_VESSEL_API_URL}/${vesselId}/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating general info of vessel:', error)
    throw new Error(error.response?.data?.message || 'Error creating general info of vessel')
  }
}

export const getGeneralInfoOfVessel = async (vesselId: number): Promise<VesselGeneralInfo> => {
  try {
    const response = await axios.get<VesselGeneralInfo>(`${ADDITIONAL_VESSEL_API_URL}/${vesselId}`)
    console.log("gen info", response.data)
    return response.data
  } catch (error: any) {
    const message = String(error.response?.data?.message || error.message || '').toLowerCase()
    if (error.response?.status === 404 || message.includes('not found')) {
      throw new Error('Not found')
    }
    console.error('Error fetching general info:', error)
    throw new Error('Error fetching general info')
  }
}

export const updateGeneralInfoOfVessel = async (
  vesselId: number,
  payload: VesselGeneralInfoRequest
): Promise<VesselGeneralInfo> => {
  try {
    const response = await axios.put<VesselGeneralInfo>(
      `${ADDITIONAL_VESSEL_API_URL}/${vesselId}/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating general info:', error)
    throw new Error(error.response?.data?.message || 'Error updating general info')
  }
}

/**
 * Fetch the existing “For USA Calls” data for a vessel
 */
export const getForUSACalls = async (vesselId: number): Promise<ForUSACalls> => {
  try {
    const response = await axios.get<ForUSACalls>(`${FOR_USA_CALLS_API_URL}/${vesselId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching For USA Calls data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching For USA Calls data')
  }
}

/**
 * Create a new “For USA Calls” record for a vessel
 */
export const createForUSACalls = async (
  vesselId: number,
  payload: ForUSACallsRequest
): Promise<ForUSACalls> => {
  try {
    const response = await axios.post<ForUSACalls>(
      `${FOR_USA_CALLS_API_URL}/${vesselId}/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating For USA Calls data:', error)
    throw new Error(error.response?.data?.message || 'Error creating For USA Calls data')
  }
}

/**
 * Update an existing “For USA Calls” record for a vessel
 */
export const updateForUSACalls = async (
  vesselId: number,
  payload: ForUSACallsRequest
): Promise<ForUSACalls> => {
  try {
    const response = await axios.put<ForUSACalls>(
      `${FOR_USA_CALLS_API_URL}/${vesselId}/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating For USA Calls data:', error)
    throw new Error(error.response?.data?.message || 'Error updating For USA Calls data')
  }
}



export const getSafetyHelicopter = async (vesselId: number): Promise<SafetyHelicopter> => {
  const response = await axios.get<SafetyHelicopter>(`${SAFETY_HELI_API_URL}/${vesselId}`)
  return response.data
}

export const createSafetyHelicopter = async (
  vesselId: number,
  payload: SafetyHelicopterRequest
): Promise<SafetyHelicopter> => {
  const response = await axios.post<SafetyHelicopter>(
    `${SAFETY_HELI_API_URL}/${vesselId}/create`,
    payload
  )
  return response.data
}

export const updateSafetyHelicopter = async (
  vesselId: number,
  payload: SafetyHelicopterRequest
): Promise<SafetyHelicopter> => {
  const response = await axios.put<SafetyHelicopter>(
    `${SAFETY_HELI_API_URL}/${vesselId}/update`,
    payload
  )
  return response.data
}

/**
 * Fetch existing Tank Coating / Anodes for a vessel
 */
export const getTankCoating = async (vesselId: number): Promise<TankCoating> => {
  try {
    const response = await axios.get<TankCoating>(`${TANK_COATING_API_URL}/${vesselId}`)
    return response.data
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Not found')
    }
    console.error('Error fetching Tank Coating:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Tank Coating')
  }
}

/**
 * Create a new Tank Coating / Anodes record
 */
export const createTankCoating = async (
  vesselId: number,
  payload: TankCoatingRequest
): Promise<TankCoating> => {
  try {
    const response = await axios.post<TankCoating>(
      `${TANK_COATING_API_URL}/${vesselId}/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating Tank Coating:', error)
    throw new Error(error.response?.data?.message || 'Error creating Tank Coating')
  }
}

/**
 * Update an existing Tank Coating / Anodes record
 */
export const updateTankCoating = async (
  vesselId: number,
  payload: TankCoatingRequest
): Promise<TankCoating> => {
  try {
    const response = await axios.put<TankCoating>(
      `${TANK_COATING_API_URL}/${vesselId}/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating Tank Coating:', error)
    throw new Error(error.response?.data?.message || 'Error updating Tank Coating')
  }
}

/**
 * Fetch existing Ballast data for a vessel
 */
export const getBallast = async (vesselId: number): Promise<Ballast> => {
  try {
    const response = await axios.get<Ballast>(`${BALLAST_API_BASE}/${vesselId}/ballast`)
    return response.data
  } catch (error: any) {
    // not-yet-created returns 404
    if (error.response?.status === 404) {
      throw new Error('Not found')
    }
    console.error('Error fetching Ballast data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Ballast data')
  }
}

/**
 * Create a new Ballast record for a vessel
 */
export const createBallast = async (
  vesselId: number,
  payload: BallastRequest
): Promise<Ballast> => {
  try {
    const response = await axios.post<Ballast>(
    `${BALLAST_API_BASE}/${vesselId}/ballast/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating Ballast data:', error)
    throw new Error(error.response?.data?.message || 'Error creating Ballast data')
  }
}

/**
 * Update an existing Ballast record for a vessel
 */
export const updateBallast = async (
  vesselId: number,
  payload: BallastRequest
): Promise<Ballast> => {
  try {
    const response = await axios.put<Ballast>(
    `${BALLAST_API_BASE}/${vesselId}/ballast/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating Ballast data:', error)
    throw new Error(error.response?.data?.message || 'Error updating Ballast data')
  }
}


/**
 * GET   /v1/vessels/{vesselId}/cargo-system
 */
export const getCargoSystem = async (vesselId: number): Promise<CargoSystem> => {
  try {
    const response = await axios.get<CargoSystem>(
      `${CARGO_API_BASE}/${vesselId}/cargo-system`
    )
    return response.data
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Not found')
    }
    console.error('Error fetching Cargo System data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Cargo System data')
  }
}

/**
 * POST  /v1/vessels/{vesselId}/cargo-system/create
 */
export const createCargoSystem = async (
  vesselId: number,
  payload: CargoSystemRequest
): Promise<CargoSystem> => {
  try {
    const response = await axios.post<CargoSystem>(
      `${CARGO_API_BASE}/${vesselId}/cargo-system/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating Cargo System data:', error)
    throw new Error(error.response?.data?.message || 'Error creating Cargo System data')
  }
}

/**
 * PUT   /v1/vessels/{vesselId}/cargo-system/update
 */
export const updateCargoSystem = async (
  vesselId: number,
  payload: CargoSystemRequest
): Promise<CargoSystem> => {
  try {
    const response = await axios.put<CargoSystem>(
      `${CARGO_API_BASE}/${vesselId}/cargo-system/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating Cargo System data:', error)
    throw new Error(error.response?.data?.message || 'Error updating Cargo System data')
  }
}

/**
 * GET   /v1/vessels/{vesselId}/vacuum-system
 */
export const getVacuumSystem = async (vesselId: number): Promise<VacuumSystem> => {
  try {
    const response = await axios.get<VacuumSystem>(
      `${VACUUM_API_BASE}/${vesselId}/vacuum-system`
    )
    return response.data
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Not found')
    }
    console.error('Error fetching Vacuum System data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Vacuum System data')
  }
}

/**
 * POST  /v1/vessels/{vesselId}/vacuum-system/create
 */
export const createVacuumSystem = async (
  vesselId: number,
  payload: VacuumSystemRequest
): Promise<VacuumSystem> => {
  try {
    const response = await axios.post<VacuumSystem>(
      `${VACUUM_API_BASE}/${vesselId}/vacuum-system/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating Vacuum System data:', error)
    throw new Error(error.response?.data?.message || 'Error creating Vacuum System data')
  }
}

/**
 * PUT   /v1/vessels/{vesselId}/vacuum-system/update
 */
export const updateVacuumSystem = async (
  vesselId: number,
  payload: VacuumSystemRequest
): Promise<VacuumSystem> => {
  try {
    const response = await axios.put<VacuumSystem>(
      `${VACUUM_API_BASE}/${vesselId}/vacuum-system/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating Vacuum System data:', error)
    throw new Error(error.response?.data?.message || 'Error updating Vacuum System data')
  }
}


export const getVesselLanding = async (vesselId: number): Promise<VesselLandingResponse> => {
  try {
    const res = await axios.get<VesselLandingResponse>(`${VESSEL_LANDING_API_URL}/${vesselId}`)
    return res.data
  } catch (error: any) {
    console.error('Error fetching vessel landing data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel landing data')
  }
}

// ---- Vessel image (returns a single image) ----
// Tries to fetch a blob image (preferred). If server returns JSON with {url},
// it will fall back to that.
export const getVesselImageUrl = async (vesselId: number): Promise<string | null> => {
  try {
    const url = `${API_URL}/vessels/${vesselId}/images`;

    // First attempt: as BLOB (image)
    const blobResp = await axios.get(url, { responseType: 'blob' });
    // If server actually answered JSON, blob.type will usually be application/json
    if (blobResp?.data instanceof Blob && blobResp.data.type?.startsWith('image/')) {
      const objectUrl = URL.createObjectURL(blobResp.data);
      return objectUrl; // call site should revokeObjectURL on unmount
    }

    // Fallback: try JSON with { url: string } or plain string
    try {
      // Convert blob to text then JSON
      const text = await blobResp.data.text?.();
      if (text) {
        const maybe = JSON.parse(text);
        if (typeof maybe === 'string') return maybe;
        if (typeof maybe?.url === 'string') return maybe.url;
      }
    } catch {
      /* ignore JSON parse errors */
    }

    return null;
  } catch (err: any) {
    // Final fallback: try normal JSON request — some servers only send JSON
    try {
      const jsonResp = await axios.get(`${API_URL}/vessels/${vesselId}/images`);
      const data = jsonResp.data;
      if (!data) return null;
      if (typeof data === 'string') return data;
      if (Array.isArray(data) && data.length && typeof data[0] === 'string') return data[0];
      if (typeof data?.url === 'string') return data.url;
      return null;
    } catch (e) {
      console.error('Error fetching vessel image:', e);
      return null;
    }
  }
};

export const replaceVesselImage = async (vesselId: number, file: File): Promise<void> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    await axios.put(`${API_URL}/vessels/${vesselId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } catch (error: any) {
    console.error('Error uploading vessel image:', error)
    throw new Error(error.response?.data?.message || 'Error uploading vessel image')
  }
}


/**
 * GET   /v1/vessels/{vesselId}/propulsion
 */
export const getPropulsionSystem = async (vesselId: number): Promise<PropulsionSystem> => {
  try {
    const response = await axios.get<PropulsionSystem>(
      `${PROPULSION_API_BASE}/${vesselId}/propulsion`
    )
    return response.data
  } catch (error: any) {
    if (error.response?.status === 404) throw new Error('Not found')
    console.error('Error fetching Propulsion System data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Propulsion data')
  }
}

/**
 * POST  /v1/vessels/{vesselId}/propulsion/create
 */
export const createPropulsionSystem = async (
  vesselId: number,
  payload: PropulsionSystemRequest
): Promise<PropulsionSystem> => {
  try {
    const response = await axios.post<PropulsionSystem>(
      `${PROPULSION_API_BASE}/${vesselId}/propulsion/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating Propulsion System data:', error)
    throw new Error(error.response?.data?.message || 'Error creating Propulsion data')
  }
}

/**
 * PUT   /v1/vessels/{vesselId}/propulsion/update
 */
export const updatePropulsionSystem = async (
  vesselId: number,
  payload: PropulsionSystemRequest
): Promise<PropulsionSystem> => {
  try {
    const response = await axios.put<PropulsionSystem>(
      `${PROPULSION_API_BASE}/${vesselId}/propulsion/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating Propulsion System data:', error)
    throw new Error(error.response?.data?.message || 'Error updating Propulsion data')
  }
}

/**
 * GET   /v1/vessels/{vesselId}/tanks-option
 */
export const getTanksOption = async (vesselId: number): Promise<VesselTanksOption> => {
  try {
    const response = await axios.get<VesselTanksOption>(
      `${TANKS_OPTION_API_BASE}/${vesselId}/tanks-option`
    )
    return response.data
  } catch (error: any) {
    if (error.response?.status === 404) throw new Error('Not found')
    console.error('Error fetching Tanks Option data:', error)
    throw new Error(error.response?.data?.message || 'Error fetching Tanks Option data')
  }
}

/**
 * POST  /v1/vessels/{vesselId}/tanks-option/create
 */
export const createTanksOption = async (
  vesselId: number,
  payload: VesselTanksOptionRequest
): Promise<VesselTanksOption> => {
  try {
    const response = await axios.post<VesselTanksOption>(
      `${TANKS_OPTION_API_BASE}/${vesselId}/tanks-option/create`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error creating Tanks Option data:', error)
    throw new Error(error.response?.data?.message || 'Error creating Tanks Option data')
  }
}

/**
 * PUT   /v1/vessels/{vesselId}/tanks-option/update
 */
export const updateTanksOption = async (
  vesselId: number,
  payload: VesselTanksOptionRequest
): Promise<VesselTanksOption> => {
  try {
    const response = await axios.put<VesselTanksOption>(
      `${TANKS_OPTION_API_BASE}/${vesselId}/tanks-option/update`,
      payload
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating Tanks Option data:', error)
    throw new Error(error.response?.data?.message || 'Error updating Tanks Option data')
  }
}


// --- MENUS TREE (permissions) ------------------------------------------------
// general (not used in modal anymore, but keep if other parts need it)
const MENUS_TREE_API_URL = `${API_URL}/menus/tree`
const PROCUREMENT_PERMISSIONS_API_URL = `${API_URL}/procurement/permissions`

// user-specific (used in permissions modal)
const USER_MENUS_TREE_ABSOLUTE  = `${API_URL}/menus/tree` // /:userId

/**
 * GET /api/menus/tree
 * Returns the dynamic menu tree used for permissions (MODULE -> MENU -> [children]).
 * Normalizes the response to an array of MenuTreeNode for convenience.
 */
export const getMenusTree = async (opts?: { token?: string }): Promise<MenuTreeNode[]> => {
  try {
    const response = await axios.get<MenuTreeNode[] | MenuTreeNode>(MENUS_TREE_API_URL, {
      headers: opts?.token ? { Authorization: `Bearer ${opts.token}` } : undefined,
    })
    const data = response.data
    return Array.isArray(data) ? data : [data]
  } catch (error: any) {
    console.error('Error fetching menus tree:', error)
    throw new Error(error.response?.data?.message || 'Error fetching menus tree')
  }
}

/**
 * GET /api/menus/tree/{userId}
 * Loads a USER-specific MENU -> SUBMENU tree with permission flags.
 */
export const getUserMenusTree = async (userId: number): Promise<MenuTreeNode[]> => {
  try {
    const url = `${USER_MENUS_TREE_ABSOLUTE}/${userId}`
    const response = await axios.get<MenuTreeNode[] | MenuTreeNode>(url)
    const data = response.data
    return Array.isArray(data) ? data : [data]
  } catch (error: any) {
    console.error('Error fetching user menu tree:', error)
    throw mgmtErr(error, 'Error fetching user menu tree')
  }
}

export type ProcurementPermissionScope = 'ALL' | 'COMPANY' | 'VESSEL'
export type ProcurementPermissionResolutionSource =
  | 'ROLE'
  | 'USER_ALLOW'
  | 'USER_DENY'
  | 'USER_MENU_ALLOW'
  | 'USER_MENU_DENY'
  | 'SUPERADMIN_DEFAULT'

export type ProcurementResolvedPermissionDto = {
  allowed: boolean
  scope: ProcurementPermissionScope | null
  source: ProcurementPermissionResolutionSource
}

export type ProcurementEffectivePermissionsDto = {
  userId: number
  roleId: number | null
  roleName: string | null
  businessRoleName?: string | null
  permissions: Record<string, ProcurementResolvedPermissionDto>
}

export type ProcurementPermissionOverrideUpdateDto = {
  permissionCode: string
  allowed: boolean
}

export const getUserProcurementPermissions = async (
  userId: number
): Promise<ProcurementEffectivePermissionsDto> => {
  try {
    const res = await axios.get<ProcurementEffectivePermissionsDto>(
      `${PROCUREMENT_PERMISSIONS_API_URL}/users/${userId}`
    )
    return res.data
  } catch (error: any) {
    console.error('Error fetching user procurement permissions:', error)
    throw new Error(error.response?.data?.message || 'Error fetching user procurement permissions')
  }
}

export const putUserProcurementPermissionOverrides = async (
  userId: number,
  updates: ProcurementPermissionOverrideUpdateDto[]
): Promise<ProcurementEffectivePermissionsDto> => {
  try {
    const res = await axios.put<ProcurementEffectivePermissionsDto>(
      `${PROCUREMENT_PERMISSIONS_API_URL}/users/${userId}/overrides`,
      updates
    )
    return res.data
  } catch (error: any) {
    console.error('Error updating user procurement permission overrides:', error)
    throw new Error(error.response?.data?.message || 'Error updating user procurement permission overrides')
  }
}


// OPERATORS ROLE 6

// GET /api/operators
export const getOperators = async (): Promise<Operator[]> => {
  try {
    const res = await axios.get<Operator[]>(OPERATORS_API_URL)
    return res.data
  } catch (error: any) {
    console.error('Error fetching operators:', error)
    throw new Error(error.response?.data?.message || 'Error fetching operators')
  }
}

// POST /api/operators/create
export const createOperator = async (payload: CreateOperatorRequest): Promise<Operator> => {
  try {
    const res = await axios.post<Operator>(`${OPERATORS_API_URL}/create`, payload)
    return res.data
  } catch (error: any) {
    console.error('Error creating operator:', error)
    throw mgmtErr(error, 'Error creating operator')
  }
}

export const createCompanyOwner = async (payload: CreateCompanyOwnerRequest): Promise<any> => {
  try {
    const res = await axios.post<any>(`${SHORES_API_URL}/create`, {
      username: payload.username,
      password: payload.password,
      name: payload.name,
      roleId: Number(payload.roleId),
      companyGroupAdminId:
        payload.companyGroupAdminId != null ? Number(payload.companyGroupAdminId) : undefined,
      companyAdminId:
        payload.companyAdminId != null ? Number(payload.companyAdminId) : undefined,
    })
    return res.data
  } catch (error: any) {
    console.error('Error creating company owner:', error)
    throw new Error(error.response?.data?.message || 'Error creating company owner')
  }
}

// PUT /api/operators/{id}
export const updateOperator = async (id: number, payload: UpdateOperatorRequest): Promise<Operator> => {
  try {
    const res = await axios.put<Operator>(`${OPERATORS_API_URL}/${id}`, payload)
    return res.data
  } catch (error: any) {
    console.error('Error updating operator:', error)
    throw mgmtErr(error, 'Error updating operator')
  }
}

// --- ACL (permissions) -------------------------------------------------------
export type AclOverride = {
  moduleId: number
  menuId: number | null
  submenuId: number | null
  subsubmenuId: number | null
  viewOverride: boolean | null
  addOverride: boolean | null
  editOverride: boolean | null
  deleteOverride: boolean | null
  downloadOverride?: boolean | null
}


export type BulkAclItem = {
  userId: number
  replaceAll: boolean
  acls: AclOverride[]
}

// API: PUT /api/acl/bulk
type AclRowResponse = AclOverride & { id: number; userId: number }

export const putBulkAcl = async (payload: BulkAclItem[]): Promise<Record<string, AclRowResponse[]>> => {
  try {
    const res = await axios.put<Record<string, AclRowResponse[]>>(`${API_URL}/acl/bulk`, payload)
    return res.data
  } catch (error: any) {
    console.error('Error updating ACLs:', error)
    throw new Error(error.response?.data?.message || 'Error updating ACLs')
  }
}

// --- VENDORS -----------------------------------------------------------------
const VENDORS_API_URL = `${API_URL}/vendors`
const VENDOR_CATEGORIES_API_URL = `${API_URL}/vendor-categories`
const VENDOR_COMPANY_GROUP_OPTIONS_URL = `${VENDORS_API_URL}/company-group-options`
const VENDOR_IMPORT_TEMPLATE_URL = `${VENDORS_API_URL}/import/template`
const VENDOR_IMPORT_PREVIEW_URL = `${VENDORS_API_URL}/import/preview`
const VENDOR_IMPORT_URL = `${VENDORS_API_URL}/import`
const REGIONS_API_URL = `${API_URL}/regions`

export type VendorCompanyGroupOption = {
  id: number
  name: string
}

export const getVendorCompanyGroupOptions = async (): Promise<VendorCompanyGroupOption[]> => {
  try {
    const res = await axios.get<VendorCompanyGroupOption[]>(VENDOR_COMPANY_GROUP_OPTIONS_URL)
    return Array.isArray(res.data) ? res.data : []
  } catch (error: any) {
    console.error('Error fetching vendor company group options:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company group options')
  }
}

export const getVendorCategories = async (): Promise<VendorCategory[]> => {
  try {
    const res = await axios.get<VendorCategory[]>(VENDOR_CATEGORIES_API_URL)
    return res.data
  } catch (error: any) {
    console.error('Error fetching vendor categories:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vendor categories')
  }
}

export const downloadVendorImportTemplate = async (format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> => {
  try {
    const res = await axios.get(VENDOR_IMPORT_TEMPLATE_URL, {
      responseType: 'blob',
      params: { format },
    })
    return res.data
  } catch (error: any) {
    console.error('Error downloading vendor import template:', error)
    throw new Error(error.response?.data?.message || 'Error downloading vendor import template')
  }
}

export const previewVendorImport = async (
  file: File,
  companyGroupAdminId?: number | null,
  companyAdminId?: number | null
): Promise<VendorImportPreview> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    if (companyGroupAdminId != null) {
      formData.append('companyGroupAdminId', String(companyGroupAdminId))
    }
    if (companyAdminId != null) {
      formData.append('companyAdminId', String(companyAdminId))
    }
    const res = await axios.post<VendorImportPreview>(VENDOR_IMPORT_PREVIEW_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  } catch (error: any) {
    console.error('Error previewing vendor import:', error)
    throw new Error(error.response?.data?.message || 'Error previewing vendor import')
  }
}

export const bulkImportVendors = async (
  file: File,
  companyGroupAdminId?: number | null,
  companyAdminId?: number | null
): Promise<VendorImportResult> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    if (companyGroupAdminId != null) {
      formData.append('companyGroupAdminId', String(companyGroupAdminId))
    }
    if (companyAdminId != null) {
      formData.append('companyAdminId', String(companyAdminId))
    }
    const res = await axios.post<VendorImportResult>(VENDOR_IMPORT_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  } catch (error: any) {
    console.error('Error bulk importing vendors:', error)
    throw new Error(error.response?.data?.message || 'Error bulk importing vendors')
  }
}

export const getVendors = async (): Promise<Vendor[]> => {
  try {
    const res = await axios.get<any>(VENDORS_API_URL, {
      params: {page: 0, size: 1000},
    })
    const data = res.data
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.content)) return data.content
    if (Array.isArray(data?.items)) return data.items
    return []
  } catch (error: any) {
    console.error('Error fetching vendors:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vendors')
  }
}

export type CurrencyOption = {
  code: string
  displayName: string
  symbol?: string | null
  decimalPlaces: number
  active: boolean
}

export const getCurrencies = async (): Promise<CurrencyOption[]> => {
  try {
    const res = await axios.get<CurrencyOption[]>(`${CURRENCIES_API_URL}/active`)
    return Array.isArray(res.data) ? res.data : []
  } catch (error: any) {
    console.error('Error fetching currencies:', error)
    throw new Error(error.response?.data?.message || 'Error fetching currencies')
  }
}

export const getVendorDetails = async (id: number): Promise<Vendor> => {
  try {
    const res = await axios.get<Vendor>(`${VENDORS_API_URL}/${id}/details`)
    return res.data
  } catch (error: any) {
    console.error('Error fetching vendor details:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vendor details')
  }
}

export const getVendorDeliveryHistory = async (
  id: number,
  page = 0,
  size = 5,
  category?: string
): Promise<VendorDeliveryPage> => {
  try {
    const res = await axios.get<VendorDeliveryPage>(`${VENDORS_API_URL}/${id}/delivery-history`, {
      params: {page, size, ...(category ? {category} : {})},
    })
    return res.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Error fetching vendor delivery history')
  }
}

export const createVendor = async (payload: CreateVendorRequest): Promise<Vendor> => {
  try {
    const res = await axios.post<Vendor>(`${VENDORS_API_URL}/create`, payload)
    return res.data
  } catch (error: any) {
    console.error('Error creating vendor:', error)
    throw new Error(error.response?.data?.message || 'Error creating vendor')
  }
}

export const updateVendor = async (id: number, payload: UpdateVendorRequest): Promise<Vendor> => {
  try {
    const res = await axios.put<Vendor>(`${VENDORS_API_URL}/${id}`, payload)
    return res.data
  } catch (error: any) {
    console.error('Error updating vendor:', error)
    throw new Error(error.response?.data?.message || 'Error updating vendor')
  }
}

export const searchRegions = async (query: string): Promise<RegionOption[]> => {
  if (!query.trim()) return []
  const res = await axios.get<RegionOption[]>(`${REGIONS_API_URL}/search`, {params: {query}})
  return Array.isArray(res.data) ? res.data : []
}

export const uploadVesselImage = async (vesselId: number, file: File): Promise<void> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    await axios.post(`${API_URL}/vessels/${vesselId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } catch (error: any) {
    console.error('Error uploading vessel image:', error)
    throw new Error(error.response?.data?.message || 'Error uploading vessel image')
  }
}

export const removeVesselImage = async (vesselId: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/vessels/${vesselId}/images`)
  } catch (error: any) {
    console.error('Error removing vessel image:', error)
    throw new Error(error.response?.data?.message || 'Error removing vessel image')
  }
}

export const getVesselParticulars = async (vesselId: number): Promise<VesselParticulars> => {
  const response = await axios.get<VesselParticulars>(`${VESSEL_PARTICULARS_API_URL}/${vesselId}`)
  return response.data
}

export const createVesselParticulars = async (
  vesselId: number,
  payload: VesselParticulars
): Promise<VesselParticulars> => {
  const response = await axios.post<VesselParticulars>(`${VESSEL_PARTICULARS_API_URL}/${vesselId}`, payload)
  return response.data
}

export const updateVesselParticulars = async (
  vesselId: number,
  payload: VesselParticulars
): Promise<VesselParticulars> => {
  const response = await axios.put<VesselParticulars>(`${VESSEL_PARTICULARS_API_URL}/${vesselId}`, payload)
  return response.data
}

export const getRegionTree = async (includeInactive = true): Promise<RegionTreeNode[]> => {
  const res = await axios.get<RegionTreeNode[]>(`${REGIONS_API_URL}/tree`, {
    params: {includeInactive},
  })
  return Array.isArray(res.data) ? res.data : []
}

export const createRegion = async (payload: RegionMutationRequest): Promise<RegionOption> => {
  const res = await axios.post<RegionOption>(REGIONS_API_URL, payload)
  return res.data
}

export const updateRegion = async (id: number, payload: RegionMutationRequest): Promise<RegionOption> => {
  const res = await axios.put<RegionOption>(`${REGIONS_API_URL}/${id}`, payload)
  return res.data
}

export const updateRegionStatus = async (id: number, active: boolean): Promise<RegionOption> => {
  const res = await axios.patch<RegionOption>(`${REGIONS_API_URL}/${id}/status`, {active})
  return res.data
}

export const deleteRegion = async (id: number): Promise<void> => {
  await axios.delete(`${REGIONS_API_URL}/${id}`)
}

export const deleteVendor = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${VENDORS_API_URL}/${id}`)
  } catch (error: any) {
    console.error('Error deleting vendor:', error)
    throw new Error(error.response?.data?.message || 'Error deleting vendor')
  }
}
